/**
 * Created by r4zi4l on 17.07.2026
 */

const PrefabEditor = function () {
    this.shadowNodes = new Map();

    this.assets = new PrefabEditorAssets();
};

PrefabEditor.prototype.initialize = function (callback) {
    this.assets.initialize(() => {
        window.addEventListener("message", this.onMessage.bind(this));

        cc.eventManager.addCustomListener(cc.Director.EVENT_AFTER_DRAW, this.onAfterDraw.bind(this));

        this.doCommand("schema.refresh");
        this.doCommand("assets.refresh");

        this.assets.loadBundle("prefab_editor")
            .then(callback);
    });
};

PrefabEditor.prototype.onAfterDraw = function () {
    if (this.currentResolutionMode !== cleverapps.resolution.mode) {
        this.currentResolutionMode = cleverapps.resolution.mode;

        this.postMessage("resolution.refreshed", {
            mode: {
                value: cleverapps.resolution.mode,
                name: PrefabEditor.WIDE_MODES[cleverapps.resolution.mode]
            }
        });
    }

    if (this.currentSelectionDirty) {
        this.currentSelectionDirty = false;

        if (this.currentSelection) {
            this.currentSelection = this.measureSelection(this.currentSelection.shadowNodeId);

            this.postMessage("selection.refreshed", this.currentSelection);
        }
    }

    if (this.currentNodeDirty) {
        this.currentNodeDirty = false;

        const shadowNode = this.currentSelection && this.getShadowNode(this.currentSelection.shadowNodeId);
        if (shadowNode) {
            this.postMessage("node.refreshed", shadowNode.calculate());
        }
    }
};

PrefabEditor.prototype.onMessage = function (event) {
    const message = event.data;
    if (!message || message.prefabEditor !== true) {
        return;
    }

    this.currentRequestId = message.requestId;
    this.currentMeta = message.meta;

    try {
        const command = PrefabEditor.COMMANDS[message.type];
        if (!command) {
            throw new Error(`unknown command "${message.type}"`);
        }

        command(this, message.data);
    } catch (error) {
        this.postMessage("error", { message: error.message });
    } finally {
        this.currentRequestId = undefined;
        this.currentMeta = undefined;
    }
};

PrefabEditor.prototype.postMessage = function (type, data, requestId, requestMeta) {
    if (window.parent === window) {
        return;
    }

    let currentRequestId = this.currentRequestId;
    let currentMeta = this.currentMeta;

    if (requestId || requestMeta) {
        currentRequestId = requestId;
        currentMeta = requestMeta;
    }

    window.parent[PrefabEditor.POST_MESSAGE]({
        prefabEditor: true,
        requestId: currentRequestId,
        type,
        data,
        meta: currentMeta
    }, "*");
};

PrefabEditor.prototype.doCommand = function (name, data) {
    const command = PrefabEditor.COMMANDS[name];
    command(this, data);
};

PrefabEditor.prototype.prepareShadowNodes = function (config) {
    const shadowNodes = new Map();

    const walk = (config, parentId) => {
        const shadowNodeId = this.createShadowNodeId();

        shadowNodes.set(config, {
            shadowNodeId,
            parentId
        });

        if (config.children) {
            config.children.forEach((childConfig) => walk(childConfig, shadowNodeId));
        }

        return shadowNodeId;
    };

    const rootShadowNodeId = walk(config.content, "root");

    shadowNodes.forEach(({ shadowNodeId, parentId }, config) => {
        const shadowNode = this.createShadowNode(shadowNodeId);
        shadowNode.initProperties(config);
        shadowNode.initParent(parentId);

        if (config.children) {
            const children = config.children.map((childConfig) => shadowNodes.get(childConfig).shadowNodeId);
            shadowNode.initChildren(children);
        }
    });

    const root = this.shadowNodes.get(rootShadowNodeId);
    root.initParams(config.params);

    return {
        params: root.serializeParams(),
        content: root.serializeContent()
    };
};

PrefabEditor.prototype.createShadowNodeId = function () {
    return Number(classManager.getNewInstanceId());
};

PrefabEditor.prototype.createShadowNode = function (shadowNodeId) {
    const shadowNode = new ShadowNode(shadowNodeId);
    this.shadowNodes.set(shadowNode.shadowNodeId, shadowNode);
    return shadowNode;
};

PrefabEditor.prototype.getShadowNode = function (shadowNodeId) {
    if (typeof shadowNodeId === "string") {
        shadowNodeId = parseInt(shadowNodeId);
    }
    return this.shadowNodes.get(shadowNodeId);
};

PrefabEditor.prototype.createPrefab = function (target) {
    const prefab = PrefabEditor.parseResource(target.prefab);
    const theme = PrefabEditor.parseResource(target.theme);

    const prefabBundle = this.assets.getRealBundleName(prefab);
    const themeBundle = theme && this.assets.getRealBundleName(theme);
    const configBundles = [];

    let prefabResource;
    let themeResource;

    return this.saveContent()
        .then(() => {
            if (this.container) {
                this.container.removeFromParent();
                this.container = undefined;

                this.prefabTarget = undefined;
                this.prefabShadowNode = undefined;
            }

            if (this.configBundles) {
                this.assets.unloadBundle(this.configBundles);
                this.configBundles = undefined;
            }

            this.shadowNodes.clear();

            return this.assets.loadBundle([prefabBundle, themeBundle]);
        })
        .then(() => {
            prefabResource = bundles[prefab.bundle].jsons[prefab.json];
            themeResource = theme && bundles[theme.bundle].jsons[theme.json];

            this.configBundles = [prefabBundle, themeBundle].filter(Boolean);

            configBundles.push(...this.assets.listConfigBundles(prefabResource.resolve().getJson()));

            if (themeResource) {
                configBundles.push(...this.assets.listConfigBundles(themeResource.resolve().getJson()));
            }

            return this.assets.loadBundle(configBundles);
        })
        .then(() => {
            const scene = cleverapps.Scenes.GetRunningScene();

            this.configBundles.push(...configBundles);

            this.container = new cc.Node();
            scene.addChild(this.container);

            const node = Prefab.instantiate(prefabResource, { editing: true, theme: themeResource });
            this.container.addChild(node);

            this.prefabShadowNode = ComponentMap.get(node, ShadowNodeBridge).getShadowNode();
            this.prefabTarget = target;

            this.transformCamera({ reset: true });

            this.doCommand("content.refresh");
        });
};

PrefabEditor.prototype.scheduleSaveContent = function () {
    if (this.saveContentTimeout) {
        clearTimeout(this.saveContentTimeout);
        this.saveContentTimeout = undefined;
    }

    this.saveContentTimeout = setTimeout(() => this.saveContent(), PrefabEditor.SAVE_CONTENT_TIMEOUT);
};

PrefabEditor.prototype.saveContent = function () {
    if (this.saveContentTimeout) {
        clearTimeout(this.saveContentTimeout);
        this.saveContentTimeout = undefined;
    }

    if (!this.prefabShadowNode) {
        return Promise.resolve();
    }

    const target = PrefabEditor.parseResource(this.prefabTarget.prefab);
    const blob = new Blob([`${JSON.stringify(this.export(), null, 4)}\n`], { type: "application/json" });

    return this.assets.createAssets(target.bundle, [{
        type: "jsons",
        key: target.json,
        fileName: `${target.json}.json`,
        blob
    }]).then(() => {
        this.postMessage("assets.updated", {
            changes: { [target.bundle]: this.assets.listBundle(bundles[target.bundle]) }
        });
    }).catch((error) => {
        cleverapps.throwAsync(`Failed to store prefab: ${error.message}`);
    });
};

PrefabEditor.prototype.exportChanges = function () {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    return this.saveContent()
        .then(() => {
            this.postMessage("assets.exported", { files: this.assets.exportChanges() }, requestId, requestMeta);
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.calculate = function () {
    const result = {};

    this.shadowNodes.forEach((shadowNode) => {
        result[shadowNode.shadowNodeId] = shadowNode.calculate();
    });

    return result;
};

PrefabEditor.prototype.export = function () {
    const shadowNodes = {};

    this.shadowNodes.forEach((shadowNode) => {
        shadowNodes[shadowNode.shadowNodeId] = shadowNode.serializeContent();
    });

    const build = (shadowNodeId) => {
        const shadowNode = shadowNodes[shadowNodeId];

        const node = { ...shadowNode };
        delete node.shadowNodeId;
        delete node.parent;

        if (shadowNode.children.length) {
            node.children = shadowNode.children.map(build);
        } else {
            delete node.children;
        }

        return node;
    };

    if (this.prefabShadowNode) {
        const result = {};

        const params = this.prefabShadowNode.serializeParams();
        const content = build(this.prefabShadowNode.shadowNodeId);

        if (!cleverapps.isEmpty(params)) {
            result.params = params;
        }

        if (!cleverapps.isEmpty(content)) {
            result.content = content;
        }

        return result;
    }
};

PrefabEditor.prototype.updateProperty = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    const shadowNode = this.getShadowNode(data.shadowNodeId);

    return shadowNode.setProperty(data.name, data.value)
        .then(() => {
            this.postMessage("property.updated", {
                shadowNodeId: data.shadowNodeId,
                name: data.name,
                value: data.value
            }, requestId, requestMeta);

            this.refreshSelection();
            this.refreshCurrentNode();

            this.scheduleSaveContent();
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.updateComponent = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    const shadowNode = this.getShadowNode(data.shadowNodeId);

    return shadowNode.setComponentProperty(data.component, data.name, data.value)
        .then(() => {
            this.postMessage("component.updated", {
                shadowNodeId: data.shadowNodeId,
                component: data.component,
                name: data.name,
                value: data.value
            }, requestId, requestMeta);

            this.refreshSelection();
            this.refreshCurrentNode();

            this.scheduleSaveContent();
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.addComponent = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    const shadowNode = this.getShadowNode(data.shadowNodeId);

    return shadowNode.addComponent(data.component)
        .then(() => {
            this.refreshSelection();
            this.refreshCurrentNode();

            this.scheduleSaveContent();
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.removeComponent = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    const shadowNode = this.getShadowNode(data.shadowNodeId);

    return shadowNode.removeComponent(data.component)
        .then(() => {
            this.refreshSelection();
            this.refreshCurrentNode();

            this.scheduleSaveContent();
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.placeNode = function (data) {
    let shadowNodeId = Number(data.shadowNodeId);
    const parentId = data.parent;
    const index = data.index;
    const config = data.config;

    const patch = [];

    if (config) {
        const childShadowNode = this.createShadowNode(this.createShadowNodeId());
        childShadowNode.initProperties(config);

        shadowNodeId = childShadowNode.shadowNodeId;

        patch.push({ path: `content.${childShadowNode.shadowNodeId}`, value: childShadowNode.calculate() });
    }

    const childShadowNode = this.getShadowNode(shadowNodeId);
    const oldParentShadowNode = this.getShadowNode(childShadowNode.parent);
    const newParentShadowNode = this.getShadowNode(parentId);

    const before = {
        shadowNodeId,
        parent: oldParentShadowNode && oldParentShadowNode.shadowNodeId,
        index: oldParentShadowNode && oldParentShadowNode.getChildren().indexOf(shadowNodeId)
    };

    if (oldParentShadowNode) {
        patch.push(...oldParentShadowNode.removeChild(childShadowNode));
    }

    if (newParentShadowNode) {
        patch.push(...newParentShadowNode.addChild(childShadowNode, index));
    }

    const after = {
        shadowNodeId,
        parent: newParentShadowNode && newParentShadowNode.shadowNodeId,
        index: newParentShadowNode && newParentShadowNode.getChildren().indexOf(shadowNodeId)
    };

    this.postMessage("node.placed", {
        patch,
        before,
        after
    });

    if (config) {
        this.setSelection(shadowNodeId);
    } else {
        this.refreshSelection();
        this.refreshCurrentNode();
    }

    this.scheduleSaveContent();
};

PrefabEditor.prototype.rebuildNode = function (data) {
    const shadowNode = this.getShadowNode(data.shadowNodeId);
    if (!shadowNode) {
        throw new Error(`unknown shadow node "${data.shadowNodeId}"`);
    }

    shadowNode.rebuild();

    this.refreshSelection();
    this.refreshCurrentNode();
};

PrefabEditor.prototype.createBundle = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    if (bundles[data.name]) {
        throw new Error(`bundle "${data.name}" already exists`);
    }

    Promise.resolve(this.assets.applyChanges(data.name, {}))
        .then(() => this.assets.save())
        .then(() => {
            this.postMessage("assets.updated", {
                changes: { [data.name]: this.assets.listBundle(bundles[data.name]) },
                created: { bundle: data.name }
            }, requestId, requestMeta);
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.removeBundle = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    if (!bundles[data.name]) {
        throw new Error(`unknown bundle "${data.name}"`);
    }

    Promise.resolve(this.assets.applyChanges(data.name, null))
        .then(() => this.assets.save())
        .then(() => {
            this.postMessage("assets.updated", {
                changes: { [data.name]: null }
            }, requestId, requestMeta);
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.resetAssets = function () {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    this.assets.reset()
        .then(() => {
            this.postMessage("assets.reset", {}, requestId, requestMeta);
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.createAsset = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    this.assets.createAssets(data.bundle, data.files)
        .then((created) => {
            this.postMessage("assets.updated", {
                changes: { [data.bundle]: this.assets.listBundle(bundles[data.bundle]) },
                created: { bundle: data.bundle, type: created.type, key: created.key }
            }, requestId, requestMeta);
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.removeAsset = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    this.assets.removeAsset(data.bundle, data.type, data.key)
        .then(() => {
            this.postMessage("assets.updated", {
                changes: { [data.bundle]: this.assets.listBundle(bundles[data.bundle]) }
            }, requestId, requestMeta);
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.previewAsset = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    this.assets.previewAsset(data.path)
        .then((preview) => {
            this.postMessage("assets.previewed", { path: data.path, preview }, requestId, requestMeta);
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.updateParams = function (data) {
    const requestId = this.currentRequestId;
    const requestMeta = this.currentMeta;

    this.prefabShadowNode.setParameter(data.name, data.value);

    return this.createPrefab(this.prefabTarget)
        .then(() => {
            this.setSelection(this.prefabShadowNode.shadowNodeId);
        })
        .catch((error) => {
            this.postMessage("error", { message: error.message }, requestId, requestMeta);
        });
};

PrefabEditor.prototype.transformCamera = function (data) {
    if (!this.container) {
        return;
    }

    const win = cc.winSize;
    const frame = cc.view.getFrameSize();

    if (data.reset) {
        const node = this.prefabShadowNode && this.prefabShadowNode.listNodes()[0];

        if (node) {
            const scene = cleverapps.Scenes.GetRunningScene();
            const box = node.getBoundingBox();

            this.container.setScale(1);

            this.container.setPosition(
                scene.width / 2 - (box.x + box.width / 2),
                scene.height / 2 - (box.y + box.height / 2)
            );
        }
    }

    if (data.move) {
        this.container.setPosition(
            this.container.x + (data.move.x || 0) * (win.width / frame.width),
            this.container.y - (data.move.y || 0) * (win.height / frame.height)
        );
    }

    if (data.zoom) {
        const factor = data.zoom.delta < 0 ? PrefabEditor.ZOOM_STEP : 1 / PrefabEditor.ZOOM_STEP;
        const scale = Math.max(PrefabEditor.MIN_ZOOM, Math.min(PrefabEditor.MAX_ZOOM, this.container.scale * factor));
        const applied = scale / this.container.scale;

        const scene = cleverapps.Scenes.GetRunningScene();
        const point = cc.p(scene.width / 2, scene.height / 2);

        this.container.setPosition(
            point.x + (this.container.x - point.x) * applied,
            point.y + (this.container.y - point.y) * applied
        );
        this.container.setScale(scale);
    }

    this.postMessage("camera.transformed", {});

    this.refreshSelection();
};

PrefabEditor.prototype.measureSelection = function (shadowNodeId) {
    const shadowNode = shadowNodeId && this.getShadowNode(shadowNodeId);
    const node = shadowNode && shadowNode.listNodes()[0];
    if (!node) {
        return null;
    }

    return {
        shadowNodeId: shadowNode.shadowNodeId,
        box: cc.rectApplyAffineTransform(node.getGlobalBoundingBox(), PrefabEditorHelper.transformToFrameSpace()),
        transform: cc.affineTransformConcat(PrefabEditorHelper.transformFromFrameSpace(), node.parent.getWorldToNodeTransform())
    };
};

PrefabEditor.prototype.setSelection = function (shadowNodeId) {
    this.currentSelection = this.measureSelection(shadowNodeId);

    this.postMessage("selection.updated", this.currentSelection);

    this.refreshSelection();
    this.refreshCurrentNode();
};

PrefabEditor.prototype.refreshSelection = function () {
    this.currentSelectionDirty = true;
};

PrefabEditor.prototype.refreshCurrentNode = function () {
    this.currentNodeDirty = true;
};

PrefabEditor.SAVE_CONTENT_TIMEOUT = 1000;

PrefabEditor.WIDE_MODES = ["vertical", "square", "horizontal"];

PrefabEditor.ZOOM_STEP = 1.1;
PrefabEditor.MIN_ZOOM = 0.1;
PrefabEditor.MAX_ZOOM = 10;

PrefabEditor.POST_MESSAGE = "postMessage";

PrefabEditor.COMMANDS = {};

PrefabEditor.registerCommand = function (name, command) {
    PrefabEditor.COMMANDS[name] = command;
};

PrefabEditor.parseResource = function (value) {
    if (!value) {
        return undefined;
    }

    const parts = value.split(".").filter((part) => part && part !== "bundles" && part !== "jsons");
    if (parts.length !== 2) {
        return undefined;
    }

    return { bundle: parts[0], json: parts[1] };
};

cleverapps.prefabEditorMode = function () {
    const params = cleverapps.getRequestParameters();
    if (!params.edit) {
        return;
    }

    cleverapps.config.demoMode = true;

    cleverapps.setRestDummies(true);

    connector.dataLoader.setEnabled(false);
    cleverapps.dataLoader.setEnabled(false);

    cleverapps.Plot.onStartup = function (f, returnClassOnly) {
        cleverapps.flags.norest = true;
        cleverapps.flags.nologin = false;

        connector.synchronizer.disable();

        if (returnClassOnly) {
            return PrefabEditorScene;
        }

        cleverapps.gameModes.noSidebar = true;
        cleverapps.gameModes.noControls = true;
        cleverapps.gameModes.noNotifications = true;
        cleverapps.gameModes.noTools = true;

        if (cleverapps.bannerAd) {
            cleverapps.bannerAd.canShow = function () {
                return false;
            };
        }

        cleverapps.prefabEditor.initialize(() => {
            cleverapps.scenes.replaceScene(new PrefabEditorScene({
                prefab: params.prefab,
                theme: params.theme
            }), f);
        });
    };
};
